import React from 'react';
import { Link } from 'react-router-dom';
import { RiFacebookFill, RiTiktokFill, RiInstagramLine, RiWhatsappFill } from 'react-icons/ri';
import { PiEnvelopeSimpleLight, PiPhoneLight, PiMapPinLight } from 'react-icons/pi';
import Cta from '../components/Cta';

const socials = [
  { href: 'https://www.facebook.com/share/1Wjd1dyHZo/', label: 'Facebook', Icon: RiFacebookFill },
  { href: 'https://tiktok.com/@leolilly.care.inte', label: 'TikTok', Icon: RiTiktokFill },
  { href: 'https://www.instagram.com/leoliliancare24/', label: 'Instagram', Icon: RiInstagramLine },
];

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/courses', label: 'Programmes' },
  { to: '/enroll', label: 'Enroll' },
  { to: '/gallery', label: 'Gallery' },
];

const Footer: React.FC = () => {
  return (
    <footer className="footer px-4 pb-8 sm:px-8 lg:px-12">
      <div className="bezel-shell-lg mx-auto max-w-7xl">
        <div className="bezel-core-lg overflow-hidden bg-espresso-800 text-cream">
          <div className="footer-content grid gap-12 px-8 py-16 sm:px-14 sm:py-20 lg:grid-cols-12">
            {/* Identity */}
            <div className="lg:col-span-5">
              <img
                src="/img/Logo.png"
                alt="Leolilian Care International"
                className="h-14 w-auto rounded-full"
              />
              <p className="mt-8 max-w-sm text-[15px] leading-relaxed text-cream-200/70">
                Founded in 2024 by the children of Leo and Lilly as a legacy-building
                institution.
              </p>

              <div className="social-media-links mt-10">
                <p className="text-[10px] uppercase tracking-eyebrow text-cream-200/50">
                  Follow us
                </p>
                <div className="social-icons mt-4 flex gap-2">
                  {socials.map(({ href, label, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="social-icon flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.06] text-cream ring-1 ring-inset ring-white/10 transition-all duration-700 ease-fluid hover:-translate-y-0.5 hover:bg-white/[0.12] active:scale-95"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="lg:col-span-3">
              <p className="text-[10px] uppercase tracking-eyebrow text-cream-200/50">
                Navigate
              </p>
              <ul className="mt-6 space-y-3">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-cream-200/75 transition-colors duration-700 ease-fluid hover:text-cream"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-eyebrow text-cream-200/50">
                Contact
              </p>
              <ul className="mt-6 space-y-4">
                <li>
                  <a
                    href="mailto:admin@leolilly.org"
                    className="flex items-center gap-3 text-sm text-cream-200/75 transition-colors duration-700 ease-fluid hover:text-cream"
                  >
                    <PiEnvelopeSimpleLight className="h-4 w-4 shrink-0" strokeWidth={1} />
                    admin@leolilly.org
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+27733544713"
                    className="flex items-center gap-3 text-sm text-cream-200/75 transition-colors duration-700 ease-fluid hover:text-cream"
                  >
                    <PiPhoneLight className="h-4 w-4 shrink-0" strokeWidth={1} />
                    +27 73 354 4713 · +27 82 689 2182
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+27672594463"
                    className="flex items-center gap-3 text-sm text-cream-200/75 transition-colors duration-700 ease-fluid hover:text-cream"
                  >
                    <PiPhoneLight className="h-4 w-4 shrink-0" strokeWidth={1} />
                    +27 67 259 4463
                  </a>
                </li>
                <li className="flex items-start gap-3 text-sm text-cream-200/75">
                  <PiMapPinLight className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1} />
                  39 Ronbex Road, Activia Park, Germiston, 1429, South Africa
                </li>
              </ul>

              <div className="mt-8">
                <Cta href="https://wa.me/27672594463" variant="light" external>
                  Chat on WhatsApp
                </Cta>
              </div>
            </div>
          </div>

          {/* Baseline */}
          <div className="flex flex-col gap-3 border-t border-white/10 px-8 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-14">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-cream-200/50">
              <span>© {new Date().getFullYear()} Leolilian Care International</span>
              <span aria-hidden="true" className="hidden sm:inline">
                ·
              </span>
              <span>Powered by Sthwalo Holdings</span>
            </p>
            <a
              href="https://wa.me/27672594463"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="whatsapp-button-footer inline-flex items-center gap-2 text-xs text-cream-200/50 transition-colors duration-700 ease-fluid hover:text-cream"
            >
              <RiWhatsappFill className="h-3.5 w-3.5" />
              Chat with us on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
